import React, { FC, memo, useEffect, useRef, useState } from 'react';
import { Dayjs } from 'dayjs';
import AddEditBalance from './AddEditBalance';
import { get } from 'src/services/GenesisApi';
import { GET_ORGANIZATIONS, MESSAGES } from 'src/constants/EUMarkets';
import { handleError } from 'src/utils/common/utils';
import { ISelectOptions } from 'src/types/Common.type';
import { IMarketBalance, IOrganization } from 'src/types/EUMarkets.type';
import { BalanceListWrapper } from '../style';

const INTERNAL_ROLE_NAME = 'Internal';

const hasRole = (organization: IOrganization, roleName: string) =>
  organization.roles?.some((role) => role.roleName === roleName);

export const getBalanceKey = (balance: IMarketBalance) =>
  [
    balance.id,
    balance.balanceName,
    balance.organizationId,
    balance.validFrom,
    balance.validTo,
  ].join('|');

interface IInlineBalanceListProps {
  open: boolean;
  marketId: number;
  balances: IMarketBalance[];
  addingBalanceCount?: number;
  marketValidFrom?: Dayjs | null;
  marketValidTo?: Dayjs | null;
  onDraftChange: (balance: IMarketBalance, rowIndex: number, error?: string) => void;
  onCancelAdd: (balanceIndex?: number) => void;
}

const InlineBalanceList: FC<IInlineBalanceListProps> = memo(
  ({
    open,
    marketId,
    balances,
    addingBalanceCount = 0,
    marketValidFrom,
    marketValidTo,
    onDraftChange,
    onCancelAdd,
  }) => {
    const [organizations, setOrganizations] = useState<ISelectOptions[]>([]);
    const [loadingOrganizations, setLoadingOrganizations] = useState(false);
    const pendingKeyIdRef = useRef(0);
    const pendingKeysRef = useRef<string[]>([]);

    while (pendingKeysRef.current.length < addingBalanceCount) {
      pendingKeysRef.current.push(`pending-balance-${pendingKeyIdRef.current}`);
      pendingKeyIdRef.current += 1;
    }

    if (pendingKeysRef.current.length > addingBalanceCount) {
      pendingKeysRef.current = pendingKeysRef.current.slice(0, addingBalanceCount);
    }

    useEffect(() => {
      if (!open) {
        return;
      }
      setLoadingOrganizations(true);
      // Only internal entities may be selected as the balance owner.
      get(GET_ORGANIZATIONS)
        .then((response) => {
          const allOrganizations = (response.data || []) as IOrganization[];
          setOrganizations(
            allOrganizations
              .filter((organization) => hasRole(organization, INTERNAL_ROLE_NAME))
              .map((organization) => ({
                id: organization.id,
                name: organization.organizationName,
              })),
          );
        })
        .catch((error) => handleError(error, MESSAGES.SERVER_ERROR))
        .finally(() => setLoadingOrganizations(false));
    }, [open]);

    return (
      <BalanceListWrapper className='parameter-inline-list'>
        <div className='balance-inline-header'>
          <span>Balance</span>
          <span>Internal Entity</span>
          <span>Valid From</span>
          <span>Valid To</span>
          <span>Actions</span>
        </div>
        {balances.map((balance, balanceIndex) => (
          <AddEditBalance
            key={`${balance.id}-${balanceIndex}`}
            open={open}
            marketId={marketId}
            balance={balance}
            organizations={organizations}
            loadingOrganizations={loadingOrganizations}
            marketValidFrom={marketValidFrom}
            marketValidTo={marketValidTo}
            onDraftChange={(draft, error) => onDraftChange(draft, balanceIndex, error)}
          />
        ))}
        {Array.from({ length: addingBalanceCount }, (_, pendingIndex) => {
          const rowIndex = balances.length + pendingIndex;
          return (
            <AddEditBalance
              key={pendingKeysRef.current[pendingIndex]}
              open={open}
              marketId={marketId}
              balance={null}
              organizations={organizations}
              loadingOrganizations={loadingOrganizations}
              marketValidFrom={marketValidFrom}
              marketValidTo={marketValidTo}
              onClose={() => {
                pendingKeysRef.current.splice(pendingIndex, 1);
                onCancelAdd(rowIndex);
              }}
              onDraftChange={(draft, error) => onDraftChange(draft, rowIndex, error)}
            />
          );
        })}
      </BalanceListWrapper>
    );
  },
);

InlineBalanceList.displayName = 'InlineBalanceList';

export default InlineBalanceList;
